import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { Avatar } from '../Avatar';
import { Logo } from '../Logo';

export interface AvatarLogoLockupProps {
  /**
   * Image source URL for the avatar
   */
  avatarSrc?: string;
  /**
   * Initials to display in avatar when no image is provided
   */
  avatarInitials?: string;
  /**
   * Alt text for the avatar image
   */
  avatarAlt?: string;
  /**
   * Click handler for the avatar section
   */
  onAvatarClick?: () => void;
  /**
   * Click handler for the logo section
   */
  onLogoClick?: () => void;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
}

/**
 * AvatarLogoLockup component - Arbor Design System
 *
 * A navigation component combining user avatar with Arbor logo,
 * typically used in the top navigation bar.
 */
export const AvatarLogoLockup = React.forwardRef<HTMLDivElement, AvatarLogoLockupProps>(
  (
    {
      avatarSrc,
      avatarInitials,
      avatarAlt = 'User avatar',
      onAvatarClick,
      onLogoClick,
      className,
      style,
    },
    ref
  ) => {
    const [isHovered, setIsHovered] = React.useState(false);

    const containerStyles: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px',
      height: '40px',
      backgroundColor: isHovered ? '#f8f8f8' : '#ffffff',
      border: '1px solid #efefef',
      borderRadius: '99px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      boxSizing: 'border-box',
      transition: 'background-color 0.15s ease-in-out',
      ...style,
    };

    const avatarSectionStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      borderRadius: '99px',
      cursor: onAvatarClick ? 'pointer' : 'default',
      backgroundColor: 'transparent',
      border: 'none',
      padding: 0,
    };

    const dividerStyles: React.CSSProperties = {
      width: '1px',
      height: '24px',
      backgroundColor: '#efefef',
      flexShrink: 0,
    };

    const logoSectionStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      cursor: onLogoClick ? 'pointer' : 'default',
      padding: 0,
    };

    const AvatarSection = onAvatarClick ? 'button' : 'div';
    const LogoSection = onLogoClick ? 'button' : 'div';

    return (
      <div
        ref={ref}
        className={className}
        style={containerStyles}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <AvatarSection
          style={{
            ...avatarSectionStyles,
            ...(onAvatarClick ? { outline: 'none' } : {}),
          }}
          onClick={onAvatarClick}
          {...(onAvatarClick ? { type: 'button' } : {})}
        >
          <Avatar
            size="medium"
            src={avatarSrc}
            initials={avatarInitials}
            alt={avatarAlt}
          />
          <ChevronDown size={12} color="#2f2f2f" strokeWidth={2} />
        </AvatarSection>

        <div style={dividerStyles} />

        <LogoSection
          style={{
            ...logoSectionStyles,
            ...(onLogoClick ? { outline: 'none', backgroundColor: 'transparent', border: 'none' } : {}),
          }}
          onClick={onLogoClick}
          {...(onLogoClick ? { type: 'button' } : {})}
        >
          <Logo withText={true} light={true} height={25} />
        </LogoSection>
      </div>
    );
  }
);

AvatarLogoLockup.displayName = 'AvatarLogoLockup';
